import React, { useState, useEffect } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions, Button,
    TextField, Box, Typography, Avatar, Chip, IconButton,
    Select, MenuItem, List, ListItem, ListItemAvatar, ListItemText,
    ListItemSecondaryAction, CircularProgress, Divider, useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Delete as DeleteIcon, PersonAdd as PersonAddIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { backendClient } from '../../../api/backend';
import { backendUrl } from '../../../config';

const ShareWorkspaceDialog = ({ open, onClose, workspace }) => {
    const theme = useTheme();
    const { t } = useTranslation();
    const [members, setMembers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [inviteEmail, setInviteEmail] = useState('');
    const [inviteRole, setInviteRole] = useState('viewer');
    const [inviting, setInviting] = useState(false);
    const goldAccent = '#c9a96e';

    // Determine API base URL based on type (workspace or folder)
    const shareType = workspace?._shareType || 'workspace';
    const isFolder = shareType === 'folder';
    const membersUrl = isFolder
        ? `${backendUrl}api/contract/workspace/folders/${workspace?.id}/members`
        : `${backendUrl}api/contract/workspace/workspaces/${workspace?.id}/members`;
    const dialogTitle = isFolder
        ? t('repository.share.titleFolder', 'Share Folder')
        : t('repository.share.title', 'Share Workspace');

    // Fetch members when dialog opens
    useEffect(() => {
        if (open && workspace) fetchMembers();
    }, [open, workspace]);

    const fetchMembers = async () => {
        setLoading(true);
        try {
            const response = await fetch(membersUrl, { headers: backendClient.authHeader() });
            if (response.ok) setMembers(await response.json());
        } catch (err) { console.error('Error fetching members:', err); }
        setLoading(false);
    };

    const handleInvite = async () => {
        if (!inviteEmail.trim()) return;
        setInviting(true);
        try {
            const response = await fetch(membersUrl, {
                    method: 'POST',
                    headers: backendClient.authHeader(),
                    body: JSON.stringify({ email: inviteEmail.trim(), role: inviteRole }),
                }
            );
            if (response.ok) {
                setInviteEmail('');
                fetchMembers();
            } else {
                const err = await response.json();
                alert(err.detail || 'Failed to invite');
            }
        } catch (err) { console.error('Error inviting:', err); }
        setInviting(false);
    };

    const handleRoleChange = async (userId, newRole) => {
        try {
            await fetch(`${membersUrl}/${userId}`, {
                    method: 'PUT',
                    headers: backendClient.authHeader(),
                    body: JSON.stringify({ role: newRole }),
                }
            );
            fetchMembers();
        } catch (err) { console.error('Error updating role:', err); }
    };

    const handleRemove = async (userId) => {
        try {
            await fetch(`${membersUrl}/${userId}`,
                { method: 'DELETE', headers: backendClient.authHeader() }
            );
            fetchMembers();
        } catch (err) { console.error('Error removing member:', err); }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth
            PaperProps={{ sx: { bgcolor: theme.palette.background.paper, borderRadius: '12px' } }}
        >
            <DialogTitle sx={{ fontSize: '1rem', fontWeight: 600, pb: 1 }}>
                {dialogTitle}
                {workspace && (
                    <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mt: 0.5 }}>
                        {workspace.name}
                    </Typography>
                )}
            </DialogTitle>

            <DialogContent>
                {/* Invite section */}
                <Box sx={{ display: 'flex', gap: 1, mb: 2, mt: 1 }}>
                    <TextField
                        size="small"
                        placeholder={t('repository.share.emailPlaceholder', 'Enter email address')}
                        value={inviteEmail}
                        onChange={(e) => setInviteEmail(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleInvite()}
                        sx={{ flex: 1 }}
                    />
                    <Select
                        size="small"
                        value={inviteRole}
                        onChange={(e) => setInviteRole(e.target.value)}
                        sx={{ minWidth: 100 }}
                    >
                        <MenuItem value="viewer">{t('repository.share.viewer', 'Viewer')}</MenuItem>
                        <MenuItem value="editor">{t('repository.share.editor', 'Editor')}</MenuItem>
                        <MenuItem value="admin">{t('repository.share.admin', 'Admin')}</MenuItem>
                    </Select>
                    <Button
                        variant="contained"
                        size="small"
                        onClick={handleInvite}
                        disabled={!inviteEmail.trim() || inviting}
                        startIcon={inviting ? <CircularProgress size={14} /> : <PersonAddIcon />}
                        sx={{ bgcolor: goldAccent, '&:hover': { bgcolor: alpha(goldAccent, 0.85) }, textTransform: 'none' }}
                    >
                        {t('repository.share.invite', 'Invite')}
                    </Button>
                </Box>

                <Divider sx={{ mb: 1 }} />

                {/* Members list */}
                <Typography variant="subtitle2" sx={{ mb: 1, color: theme.palette.text.secondary, fontSize: '0.75rem', textTransform: 'uppercase' }}>
                    {t('repository.share.members', 'Members')} ({members.length})
                </Typography>

                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                        <CircularProgress size={24} />
                    </Box>
                ) : (
                    <List dense>
                        {members.map((member) => (
                            <ListItem key={member.user_id} sx={{ px: 0, py: 0.75 }}>
                                <ListItemAvatar sx={{ minWidth: 40 }}>
                                    <Avatar sx={{ width: 32, height: 32, fontSize: '0.75rem', bgcolor: alpha(goldAccent, 0.2), color: goldAccent }}>
                                        {(member.user_name || member.user_email || '?')[0].toUpperCase()}
                                    </Avatar>
                                </ListItemAvatar>
                                <ListItemText
                                    primary={member.user_name || member.user_email}
                                    secondary={member.user_email}
                                    primaryTypographyProps={{ fontSize: '0.82rem', fontWeight: 500 }}
                                    secondaryTypographyProps={{ fontSize: '0.72rem' }}
                                />
                                <ListItemSecondaryAction sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    {member.role === 'owner' ? (
                                        <Chip label={t('repository.share.owner', 'Owner')} size="small" sx={{ height: 22, fontSize: '0.7rem' }} color="default" />
                                    ) : (
                                        <>
                                            <Select
                                                size="small"
                                                value={member.role}
                                                onChange={(e) => handleRoleChange(member.user_id, e.target.value)}
                                                sx={{ fontSize: '0.75rem', height: 28, minWidth: 90 }}
                                            >
                                                <MenuItem value="viewer" sx={{ fontSize: '0.8rem' }}>{t('repository.share.viewer', 'Viewer')}</MenuItem>
                                                <MenuItem value="editor" sx={{ fontSize: '0.8rem' }}>{t('repository.share.editor', 'Editor')}</MenuItem>
                                                <MenuItem value="admin" sx={{ fontSize: '0.8rem' }}>{t('repository.share.admin', 'Admin')}</MenuItem>
                                            </Select>
                                            <IconButton size="small" onClick={() => handleRemove(member.user_id)} sx={{ color: theme.palette.error.main }}>
                                                <DeleteIcon sx={{ fontSize: 16 }} />
                                            </IconButton>
                                        </>
                                    )}
                                </ListItemSecondaryAction>
                            </ListItem>
                        ))}
                    </List>
                )}
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose} size="small">{t('common.close', 'Close')}</Button>
            </DialogActions>
        </Dialog>
    );
};

export default ShareWorkspaceDialog;
